import { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Trash, Pencil, FolderOpen, FileCode } from 'lucide-react';

export default function NotebookDialog({ onClose }) {
  const { notebooks, templates, createNotebook, updateNotebook, deleteNotebook } = useApp();
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editTemplateId, setEditTemplateId] = useState('');

  const handleCreate = async () => {
    if (!name.trim()) return;
    await createNotebook({ name: name.trim(), default_template_id: templateId || null });
    setName('');
    setTemplateId('');
  };

  const handleUpdate = async () => {
    if (!editName.trim() || !editingId) return;
    await updateNotebook(editingId, { name: editName.trim(), default_template_id: editTemplateId || null });
    setEditingId(null);
  };

  const startEdit = (nb) => {
    setEditingId(nb.id);
    setEditName(nb.name);
    setEditTemplateId(nb.default_template_id || '');
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="notebook-dialog">
        <DialogHeader>
          <DialogTitle>Manage Notebooks</DialogTitle>
          <DialogDescription>Create folders to organize your notes. Assign a default template to auto-fill new notes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Create form */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Notebook name" className="flex-1 h-8 text-sm" onKeyDown={(e) => e.key === 'Enter' && handleCreate()} data-testid="notebook-name-input" />
              <Button onClick={handleCreate} size="sm" className="h-8" data-testid="notebook-create-btn">Create</Button>
            </div>
            {templates.length > 0 && name.trim() && (
              <div className="flex items-center gap-2 animate-fade-in">
                <FileCode className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="flex-1 h-7 px-2 text-xs bg-input border border-border rounded outline-none" data-testid="notebook-template-select">
                  <option value="">No default template</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Notebook list */}
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {notebooks.map((nb) => {
              const tmpl = templates.find((t) => t.id === nb.default_template_id);
              return (
                <div key={nb.id} className="py-1.5 px-2 rounded-sm hover:bg-muted/50" data-testid={`notebook-item-${nb.id}`}>
                  {editingId === nb.id ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 h-7 text-sm" onKeyDown={(e) => e.key === 'Enter' && handleUpdate()} />
                        <Button onClick={handleUpdate} size="sm" variant="ghost" className="h-7 text-xs">Save</Button>
                        <Button onClick={() => setEditingId(null)} size="sm" variant="ghost" className="h-7 text-xs">Cancel</Button>
                      </div>
                      {templates.length > 0 && (
                        <div className="flex items-center gap-2">
                          <FileCode className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <select value={editTemplateId} onChange={(e) => setEditTemplateId(e.target.value)} className="flex-1 h-7 px-2 text-xs bg-input border border-border rounded outline-none" data-testid={`notebook-edit-template-${nb.id}`}>
                            <option value="">No default template</option>
                            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm">{nb.name}</span>
                        {tmpl && (
                          <span className="ml-2 text-[10px] text-muted-foreground/60 bg-muted/40 px-1.5 py-0.5 rounded">
                            {tmpl.name}
                          </span>
                        )}
                      </div>
                      <button onClick={() => startEdit(nb)} className="p-1 text-muted-foreground hover:text-foreground" data-testid={`notebook-edit-${nb.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteNotebook(nb.id)} className="p-1 text-muted-foreground hover:text-red-400" data-testid={`notebook-delete-${nb.id}`}><Trash className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              );
            })}
            {notebooks.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No notebooks yet</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
